/* eslint-disable max-lines */
// deps
import classNames from "classnames";
import React, { ReactElement, useCallback, useMemo, useState } from "react";

// internal-deps
import { getCloudinaryUrl } from "@swiggy-private/core-ui";
import { CardDetails, JuspayTokenizationMeta } from "@swiggy-private/payment-types";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// components
import { SecureCardInfo } from "@payment-ui/components/secure-card-info";
import ManageCardSaveDetails from "./components/SaveDetails";
import Input from "@payment-ui/components/add-new-card/components/Input";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { cardInputs } from "./constants";

// helpers
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import { getFormattedCardName } from "@payment-ui/helpers/Card";
// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";

// test-ids
import { MANAGE_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import styles from "./ManageCard.scss";

import { useCardHalfCard } from "@payment-ui/hooks/useCardHalfCardActions";

export interface ManageCardProps {
    cardDetails: CardDetails;
    onClickSave: (nickName: string, userConsent: boolean) => void;
    juspayTokenizationMeta?: JuspayTokenizationMeta;
    userConsent?: boolean;
    onKnowMoreTokenizationClickAction?: VoidFunction;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function ManageCard(props: ManageCardProps): ReactElement {
    const {
        nickname: userNickName,
        fullImageUrl,
        iconUrl,
        isSecured,
        tokenizeSupport,
        cardBrand,
        cardExpMonth,
        cardExpYear,
        cardNumber,
    } = props.cardDetails;

    const { onKnowMoreTokenizationClickAction } = props;

    const expiry = useMemo(() => `${cardExpMonth}/${cardExpYear}`, [cardExpMonth, cardExpYear]);
    const [nickName, setNickName] = useState<string>(userNickName);

    const [userConsent, setUserConsent] = useState(false);

    const { showSecureCardInfo, onSecureCardInfoClick, closeSecureCardInfo } = useCardHalfCard({
        onKnowMoreTokenizationClickAction,
    });

    const onClickCheckbox = useCallback((): void => {
        setUserConsent(!userConsent);
    }, [userConsent]);

    const onClickSave = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();

            props.onClickSave(nickName, userConsent);
        },
        [nickName, props, userConsent],
    );

    const onKnowMoreClick = useCallback(
        (e: React.MouseEvent): void => {
            onSecureCardInfoClick(e);
        },
        [onSecureCardInfoClick],
    );

    //Card Icon CSS
    const logoClass = classNames(styles.manageCardDetailsLogo, {
        [styles.manageCardDetailsLogoShow]: cardBrand,
        [styles.manageCardDetailsLogoUx4]: true,
    });
    const imgClass = styles.manageCardDetailsLogoImgWithPadding3;

    //Card Icon & Url
    const CardFullImageUrl = useMemo(() => getCloudinaryUrl(fullImageUrl), [fullImageUrl]);
    const CardIconUrl = useMemo(() => getCloudinaryUrl(iconUrl), [iconUrl]);

    const ButtonCta = useMemo(() => {
        if (tokenizeSupport && !isSecured) {
            return TEXTS.MANAGE_CARD_SAVE_AND_SECURE_BUTTON;
        }

        return TEXTS.MANAGE_CARD_SAVE_BUTTON;
    }, [isSecured, tokenizeSupport]);

    const onNickNameChange = useCallback((value: string) => {
        setNickName(String(value).replace(/[^a-zA-z ]/gim, ""));
    }, []);

    //added to remove leading, trailing and any extra spaces in between words
    const onBlurNickName = useCallback(() => {
        if (nickName) {
            setNickName(nickName.trim().replace(/\s\s+/g, " "));
        }
    }, [nickName]);

    //Card Icon Element
    const icon = !iconUrl ? null : (
        <div className={logoClass}>
            <PaymentMethodIcon
                src={CardIconUrl}
                imgClassName={imgClass}
                parentClassName={styles.manageCardDetailsLogoParent}
            />
        </div>
    );

    const isCTADisabled = React.useMemo(() => {
        if (!userConsent && tokenizeSupport && !isSecured) {
            return true;
        }

        return false;
    }, [userConsent, tokenizeSupport, isSecured]);

    const saveCardInfoMessage = useMemo(() => {
        if (!isSecured && tokenizeSupport) {
            return TEXTS.STORE_YOUR_CARD;
        }
    }, [isSecured, tokenizeSupport]);

    const isNickNameDisabled = useMemo(() => {
        if (tokenizeSupport && !isSecured) {
            return true;
        }
        return false;
    }, [isSecured, tokenizeSupport]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.manageCard}>
                <div className={styles.manageCardHeroIcon}>
                    <img
                        alt={""}
                        crossOrigin="anonymous"
                        className={styles.manageCardHeroIconImg}
                        src={CardFullImageUrl}
                    />
                </div>

                <div className={styles.manageCardDetails}>
                    <Input
                        value={getFormattedCardName(cardNumber)}
                        {...cardInputs.cardNumber}
                        disabled
                        keepLabelActive={true}
                        onChange={(): void => void 0}
                    />
                    {icon}
                </div>
                <Input
                    value={expiry}
                    {...cardInputs.expiry}
                    disabled={true}
                    keepLabelActive={true}
                    onChange={(): void => void 0}
                />
                <div className={styles.manageCardInputContainer}>
                    <Input
                        value={nickName}
                        {...cardInputs.nickName}
                        keepLabelActive={userNickName != "" ? true : false}
                        disabled={isNickNameDisabled}
                        onChange={onNickNameChange}
                        onBlur={onBlurNickName}
                    />
                </div>

                <ManageCardSaveDetails
                    selected={userConsent}
                    onClick={onClickCheckbox}
                    onClickKnowMore={onKnowMoreClick}
                    cardBrandName={cardBrand ?? "VISA"}
                    text={saveCardInfoMessage}
                />

                {showSecureCardInfo.show && props.juspayTokenizationMeta && (
                    <SecureCardInfo
                        data={props.juspayTokenizationMeta}
                        {...showSecureCardInfo.actions}
                        onClose={closeSecureCardInfo}
                        cardBrand={capitalizeFirstLetter(cardBrand || "")}
                    />
                )}
            </div>
            <div className={styles.manageCardSaveButtonContainer}>
                <SuperButton
                    buttonType="primary"
                    buttonSize="large"
                    variant="fullwidth"
                    className={styles.manageCardSaveButton}
                    onClick={onClickSave}
                    isDisabled={isCTADisabled}
                    data-testid={MANAGE_CARD_TEST_IDS.button}
                >
                    {ButtonCta}
                </SuperButton>
            </div>
        </div>
    );
}
